import React from 'react';
import { Link } from '@inertiajs/react';
import { Users, Link2, DollarSign, CheckCircle, FileText, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  StatCard,
  DataTable,
  Badge,
  Card,
  CardHeader,
  CardBody,
} from '../../../design-system/components';
import { formatCurrency, formatRelativeTime } from '../../../lib/formatters';
import {
  admin_applications_path,
  admin_commissions_path,
  admin_fraud_root_path,
  admin_affiliates_path,
  admin_affiliate_path,
  admin_campaign_path,
} from '../../../lib/routes';
const commissionStatusVariant = {
  pending: 'warning',
  approved: 'info',
  paid: 'success',
  reversed: 'danger',
};
const commissionColumns = [
  {
    id: 'affiliate',
    header: 'Affiliate',
    render: (row) => (
      <Link href={admin_affiliate_path(row.affiliate_id)} className="font-medium text-fg hover:text-fg-secondary">
        {row.affiliate_name}
      </Link>
    ),
  },
  {
    id: 'campaign',
    header: 'Campaign',
    render: (row) => (
      <Link href={admin_campaign_path(row.campaign_id)} className="text-sm text-fg-secondary hover:text-fg">
        {row.campaign_name}
      </Link>
    ),
  },
  {
    id: 'amount',
    header: 'Amount',
    render: (row) => formatCurrency(row.commission_amount_cents),
  },
  {
    id: 'status',
    header: 'Status',
    render: (row) => (
      <Badge variant={commissionStatusVariant[row.status] ?? 'default'} size="sm">
        {row.status}
      </Badge>
    ),
  },
  {
    id: 'created_at',
    header: 'Date',
    render: (row) => formatRelativeTime(row.created_at),
  },
];
const referralColumns = [
  {
    id: 'id',
    header: 'ID',
    accessor: 'id',
  },
  {
    id: 'attribution',
    header: 'Attribution',
    render: (row) => (
      <Badge variant="default" size="sm">
        {row.attribution_type}
      </Badge>
    ),
  },
  {
    id: 'referred_at',
    header: 'Referred',
    render: (row) => formatRelativeTime(row.referred_at),
  },
  {
    id: 'converted',
    header: 'Converted',
    render: (row) =>
      row.converted_at ? (
        formatRelativeTime(row.converted_at)
      ) : (
        <span className="text-fg-tertiary">Not yet</span>
      ),
  },
];
const topAffiliateColumns = [
  {
    id: 'name',
    header: 'Name',
    render: (row) => (
      <Link
        href={admin_affiliates_path() + `/${row.id}`}
        className="font-medium text-fg hover:text-fg-secondary"
      >
        {row.name}
      </Link>
    ),
  },
  {
    id: 'slug',
    header: 'Slug',
    render: (row) => <span className="font-mono text-xs text-fg-tertiary">{row.slug}</span>,
  },
  {
    id: 'referrals',
    header: 'Referrals',
    render: (row) => row.referral_count,
  },
  {
    id: 'commissions',
    header: 'Commissions',
    render: (row) => formatCurrency(row.commission_total_cents),
  },
];
export default function Show({
  total_affiliates,
  active_affiliates,
  pending_applications,
  total_referrals,
  total_commissions_cents,
  pending_commissions_cents,
  paid_commissions_cents,
  flagged_fraud_count,
  recent_referrals,
  recent_commissions,
  top_affiliates,
}) {
  return (
    <AdminLayout>
      <PageHeader title="Dashboard" description="Overview of your affiliate program" />

      {/* Stat cards */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="Active Affiliates"
          value={active_affiliates}
          icon={<Users size={24} />}
        />
        <StatCard
          label="Total Referrals"
          value={total_referrals}
          icon={<Link2 size={24} />}
        />
        <StatCard
          label="Total Commissions"
          value={formatCurrency(total_commissions_cents)}
          icon={<DollarSign size={24} />}
        />
        <StatCard
          label="Paid Out"
          value={formatCurrency(paid_commissions_cents)}
          icon={<CheckCircle size={24} />}
        />
      </div>

      {/* Quick action cards */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {pending_applications > 0 && (
          <Link href={admin_applications_path()} className="block">
            <div className="flex items-center gap-4 rounded-lg border border-warning-200 bg-warning-50 p-4 transition-colors hover:bg-warning-100">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-200 text-warning-700">
                <FileText size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-warning-800">
                  {pending_applications} pending application{pending_applications !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-warning-600">Review and approve</p>
              </div>
            </div>
          </Link>
        )}

        {pending_commissions_cents > 0 && (
          <Link href={admin_commissions_path()} className="block">
            <div className="flex items-center gap-4 rounded-lg border border-edge-strong bg-surface-secondary p-4 transition-colors hover:bg-surface-muted">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-neutral-200 text-fg-secondary">
                <DollarSign size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-fg">
                  {formatCurrency(pending_commissions_cents)} pending
                </p>
                <p className="text-xs text-fg-secondary">Ready to pay out</p>
              </div>
            </div>
          </Link>
        )}

        {flagged_fraud_count > 0 && (
          <Link href={admin_fraud_root_path()} className="block">
            <div className="flex items-center gap-4 rounded-lg border border-danger-200 bg-danger-50 p-4 transition-colors hover:bg-danger-100">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-danger-200 text-danger-700">
                <AlertTriangle size={20} />
              </div>
              <div>
                <p className="text-sm font-medium text-danger-800">
                  {flagged_fraud_count} flagged event{flagged_fraud_count !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-danger-600">Needs review</p>
              </div>
            </div>
          </Link>
        )}
      </div>

      {/* Tables section */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Commissions */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Recent Commissions</h3>
            <Link
              href={admin_commissions_path()}
              className="text-xs font-medium text-fg hover:text-fg-secondary"
            >
              View all
            </Link>
          </CardHeader>
          <CardBody className="p-0">
            <DataTable
              columns={commissionColumns}
              data={recent_commissions}
              keyAccessor="id"
              emptyTitle="No commissions yet"
              emptyDescription="Commissions will appear here as affiliates generate referrals."
              className="border-0 rounded-none"
            />
          </CardBody>
        </Card>

        {/* Top Affiliates */}
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Top Affiliates</h3>
            <Link
              href={admin_affiliates_path()}
              className="text-xs font-medium text-fg hover:text-fg-secondary"
            >
              View all
            </Link>
          </CardHeader>
          <CardBody className="p-0">
            <DataTable
              columns={topAffiliateColumns}
              data={top_affiliates}
              keyAccessor="id"
              emptyTitle="No affiliates yet"
              emptyDescription="Affiliates will appear here once they join the program."
              className="border-0 rounded-none"
            />
          </CardBody>
        </Card>
      </div>

      {/* Recent Referrals */}
      <div className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Recent Referrals</h3>
          </CardHeader>
          <CardBody className="p-0">
            <DataTable
              columns={referralColumns}
              data={recent_referrals}
              keyAccessor="id"
              emptyTitle="No referrals yet"
              emptyDescription="Referrals will appear here as affiliates share their links."
              className="border-0 rounded-none"
            />
          </CardBody>
        </Card>
      </div>
    </AdminLayout>
  );
}
